/**
 * buildLineMap(source) → lineMap
 *
 * lineMap[tiptapChildIndex] = 1-indexed source line
 *
 * Mirrors the parser's logic to determine which source line each top-level
 * TipTap child node corresponds to, enabling line-based scroll sync between
 * the TipTap editor and the CodeMirror source pane.
 *
 * Mapping rules (matching templateParser + contentToNodes):
 *   @section     → 1 TipTap child at the directive line
 *   heading      → 1 TipTap child at the directive line
 *   @scoregroup  → 1 TipTap child at the directive line
 *   @field       → 1 TipTap child at the directive line
 *   text line    → 1 TipTap child (paragraph) per source line,
 *                  including blank lines between text/directives
 */

/** Returns the index after the closing `---` of the frontmatter block, or 0 if none. */
function findBodyStart(lines: string[]): number {
  if (lines[0]?.trim() !== "---") {
    return 0;
  }
  for (let k = 1; k < lines.length; k++) {
    if (lines[k]?.trim() === "---") {
      return k + 1;
    }
  }
  return 0;
}

/** Returns true if the trimmed line is a block-level directive or heading. */
function isBlockDirective(trimmed: string): boolean {
  return (
    trimmed.startsWith("@section(") ||
    trimmed.startsWith("@scoregroup(") ||
    /^#{1,3}\s/.test(trimmed)
  );
}

/** Flush accumulated text line numbers into result as individual entries. */
function flushText(textLineNums: number[], result: number[]): void {
  for (const ln of textLineNums) {
    result.push(ln);
  }
  textLineNums.length = 0;
}

interface FieldState {
  inField: boolean;
  awaitingLabel: boolean;
}

/** Reset inField/awaitingLabel if we are in an unsealed field context. */
function endFieldIfOpen(state: FieldState): void {
  if (state.inField && !state.awaitingLabel) {
    state.inField = false;
    state.awaitingLabel = false;
  }
}

/** Handle a blank line in the line-map state machine. */
function handleBlank(lineNum: number, state: FieldState, textLineNums: number[]): void {
  endFieldIfOpen(state);
  textLineNums.push(lineNum);
}

/** Handle a block directive (@section, @scoregroup, heading) line. */
function handleBlockDirective(
  lineNum: number,
  state: FieldState,
  textLineNums: number[],
  result: number[],
): void {
  endFieldIfOpen(state);
  flushText(textLineNums, result);
  result.push(lineNum);
}

/** Handle an @field directive line. */
function handleFieldDirective(
  lineNum: number,
  state: FieldState,
  textLineNums: number[],
  result: number[],
): void {
  flushText(textLineNums, result);
  result.push(lineNum);
  state.inField = true;
  state.awaitingLabel = true;
}

/**
 * Process a single source line in the body and update the result/textLineNums arrays
 * and the field state machine. Returns true if the line was consumed by field context
 * and should not be added to textLineNums.
 */
function processLine(
  line: string,
  lineNum: number,
  state: FieldState,
  textLineNums: number[],
  result: number[],
): void {
  const trimmed = line.trim();
  const indented = line.length > 0 && (line.startsWith(" ") || line.startsWith("\t"));

  if (trimmed === "") {
    handleBlank(lineNum, state, textLineNums);
    return;
  }

  if (isBlockDirective(trimmed)) {
    handleBlockDirective(lineNum, state, textLineNums, result);
    return;
  }

  if (trimmed.startsWith("@field(")) {
    handleFieldDirective(lineNum, state, textLineNums, result);
    return;
  }

  if (state.awaitingLabel) {
    if (!indented) {
      state.awaitingLabel = false;
    }
    return; // label line consumed by field (not a TipTap child)
  }

  if (state.inField) {
    if (indented) {
      return;
    }
    state.inField = false; // non-indented non-blank ends field body; fall through to text
  }

  textLineNums.push(lineNum);
}

export function buildLineMap(source: string): number[] {
  const lines = source.split("\n");
  if (lines.at(-1) === "") {
    lines.pop();
  }

  const result: number[] = [];
  const bodyStart = findBodyStart(lines);

  const textLineNums: number[] = [];
  const state: FieldState = { awaitingLabel: false, inField: false };

  for (let i = bodyStart; i < lines.length; i++) {
    const line = lines[i] ?? "";
    processLine(line, i + 1, state, textLineNums, result);
  }

  flushText(textLineNums, result);
  return result;
}
